import { trackMixpanelEvent, setMixpanelPeopleProperties } from './mixpanel';
import { capturePosthogEvent, setPosthogPersonProperties } from './posthog';

export type AnalyticsProps = Record<
  string,
  string | number | boolean | null | undefined
>;

export type LimitType =
  | 'templates'
  | 'send_test_daily'
  | 'exports_monthly'
  | 'live_preview_credits'
  | 'ai_credits'
  | 'images_storage'
  | 'team_members'
  | 'recipients';

const UPGRADE_SOURCE_KEY = 'postdrop_upgrade_source';
const UPGRADE_SOURCE_TTL_MS = 60 * 60 * 1000; // 1 hour
const PLAN_UPGRADED_KEY_PREFIX = 'postdrop_plan_upgraded_tracked:';

const LIMIT_TYPES: ReadonlySet<string> = new Set([
  'templates',
  'send_test_daily',
  'exports_monthly',
  'live_preview_credits',
  'ai_credits',
  'images_storage',
  'team_members',
  'recipients',
]);

type UpgradeSourcePayload = {
  v: 1;
  ts: number;
  source: string;
  limit_type?: LimitType;
};

type UpgradeSourceValue = {
  source: string;
  limit_type?: LimitType;
};

/**
 * Sends one product analytics event to both Mixpanel and PostHog.
 * Keeps dual-write call-sites consistent while we migrate GTM events to code.
 */
export const trackProductEvent = (
  eventName: string,
  eventProperties: AnalyticsProps = {}
) => {
  trackMixpanelEvent(eventName, eventProperties);
  capturePosthogEvent(eventName, eventProperties);
};

function clearLegacySessionUpgradeSource(): void {
  try {
    sessionStorage.removeItem(UPGRADE_SOURCE_KEY);
  } catch {
    /* ignore */
  }
}

function parseUpgradeSourcePayload(raw: string | null): UpgradeSourceValue | null {
  if (raw == null) {
    return null;
  }
  try {
    const parsed = JSON.parse(raw) as UpgradeSourcePayload;
    if (parsed?.v !== 1 || typeof parsed.ts !== 'number' || !parsed.source) {
      return null;
    }
    if (Date.now() - parsed.ts > UPGRADE_SOURCE_TTL_MS) {
      return null;
    }
    return {
      source: parsed.source,
      ...(parsed.limit_type ? { limit_type: parsed.limit_type } : {}),
    };
  } catch {
    return null;
  }
}

/** Persist upgrade intent so Checkout Started / Plan Upgraded can attribute the trigger. */
export function setUpgradeSource(
  source: string,
  limitType?: LimitType
): void {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    const payload: UpgradeSourcePayload = {
      v: 1,
      ts: Date.now(),
      source,
    };
    if (limitType) {
      payload.limit_type = limitType;
    }
    // localStorage is shared across tabs; sessionStorage is not (Plans often opens in a new tab).
    localStorage.setItem(UPGRADE_SOURCE_KEY, JSON.stringify(payload));
    clearLegacySessionUpgradeSource();
  } catch {
    /* localStorage may be unavailable */
  }
}

/** Read upgrade intent without clearing (e.g. Checkout Started before redirect). */
export function peekUpgradeSource(): UpgradeSourceValue | null {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    clearLegacySessionUpgradeSource();
    const value = parseUpgradeSourcePayload(
      localStorage.getItem(UPGRADE_SOURCE_KEY)
    );
    if (!value) {
      localStorage.removeItem(UPGRADE_SOURCE_KEY);
      return null;
    }
    return value;
  } catch {
    return null;
  }
}

/** Read and clear upgrade intent (e.g. after Plan Upgraded). */
export function consumeUpgradeSource(): UpgradeSourceValue | null {
  const value = peekUpgradeSource();
  if (typeof window !== 'undefined') {
    try {
      localStorage.removeItem(UPGRADE_SOURCE_KEY);
      clearLegacySessionUpgradeSource();
    } catch {
      /* ignore */
    }
  }
  return value;
}

/** Build /plans URL with attribution query params for new-tab / context-menu opens. */
export function plansUrlWithUpgradeSource(
  source: string,
  limitType?: LimitType
): string {
  const params = new URLSearchParams({ upgrade_source: source });
  if (limitType) {
    params.set('limit_type', limitType);
  }
  return `/plans?${params.toString()}`;
}

function queryParam(
  value: string | string[] | undefined
): string | undefined {
  if (typeof value === 'string' && value) {
    return value;
  }
  if (Array.isArray(value) && typeof value[0] === 'string' && value[0]) {
    return value[0];
  }
  return undefined;
}

/**
 * Hydrate upgrade attribution from /plans?upgrade_source=…&limit_type=…
 * so context-menu "Open in new tab" still attributes Checkout Started.
 */
export function hydrateUpgradeSourceFromQuery(query: {
  upgrade_source?: string | string[];
  limit_type?: string | string[];
}): void {
  const source = queryParam(query.upgrade_source);
  if (!source) {
    return;
  }
  const limitRaw = queryParam(query.limit_type);
  const limitType =
    limitRaw && LIMIT_TYPES.has(limitRaw)
      ? (limitRaw as LimitType)
      : undefined;
  setUpgradeSource(source, limitType);
}

/** Fire when the UI learns the user hit a plan gate. */
export function trackLimitHit(props: {
  limit_type: LimitType;
  plan?: string | null;
  source: string;
}): void {
  trackProductEvent('Limit Hit', {
    limit_type: props.limit_type,
    plan: props.plan ?? undefined,
    source: props.source,
  });
}

/** Fire when user clicks Upgrade / View Plans; also stores attribution. */
export function trackUpgradeCtaClicked(props: {
  source: string;
  limit_type?: LimitType;
  plan?: string | null;
}): void {
  setUpgradeSource(props.source, props.limit_type);
  trackProductEvent('Upgrade CTA Clicked', {
    source: props.source,
    limit_type: props.limit_type,
    plan: props.plan ?? undefined,
  });
}

/** Set plan / billing traits on Mixpanel people + PostHog person profiles. */
export function setAnalyticsPersonProperties(props: {
  plan?: string | null;
  has_billing?: boolean | null;
}): void {
  const traits: Record<string, string | boolean> = {};
  if (props.plan != null && props.plan !== '') {
    traits.plan = props.plan;
  }
  if (props.has_billing != null) {
    traits.has_billing = props.has_billing;
  }
  if (Object.keys(traits).length === 0) {
    return;
  }
  setMixpanelPeopleProperties(traits);
  setPosthogPersonProperties(traits);
}

/**
 * Fire Plan Upgraded once per checkout success return (deduped by session_id).
 * Call from pages that show the stripe_checkout=success banner, and only once
 * `useAuth()` has a user. That page mounts while auth is still loading, before
 * PostHog init and identify. Marking the event sent at that moment drops it.
 */
export function trackPlanUpgradedIfNeeded(opts: {
  sessionId?: string | string[] | null;
  plan?: string | null;
}): void {
  if (typeof window === 'undefined') {
    return;
  }
  const sessionKey =
    typeof opts.sessionId === 'string' && opts.sessionId
      ? opts.sessionId
      : 'success';
  const storageKey = `${PLAN_UPGRADED_KEY_PREFIX}${sessionKey}`;
  try {
    if (sessionStorage.getItem(storageKey)) {
      return;
    }
    sessionStorage.setItem(storageKey, '1');
  } catch {
    /* continue even if sessionStorage fails */
  }
  const upgrade = consumeUpgradeSource();
  trackProductEvent('Plan Upgraded', {
    plan: opts.plan ?? undefined,
    upgrade_source: upgrade?.source,
    limit_type: upgrade?.limit_type,
  });
}
